import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ChevronUp, ChevronDown, ToggleLeft, ToggleRight, Sparkles, ShieldAlert,
  Layers, ArrowLeft, Search, Check, BookOpen, RotateCcw, Settings2,
} from "lucide-react";

/**
 * Event Role Setup — Phase A organiser console.
 *
 * Flow:
 *   1. Pick a template (MUN / Youth Parl / Debate / Quiz / Model G20 / Custom).
 *   2. Enable/disable individual roles.
 *   3. Reorder roles (↑ / ↓ buttons; explicit-order, accessible, mobile-safe).
 *   4. Open the permission matrix for any role and toggle View/Create/Edit/
 *      Approve/Publish/Admin per permission. Sensitive perms show a warning.
 *
 * Phase A guardrails:
 *   • Disabled roles disappear from assignment dropdowns (Phase B owns that).
 *   • Audit-logged on every change.
 *   • Organisers can only grant permissions their own role allows.
 */
const LEVELS = ["none", "view", "create", "edit", "approve", "publish", "admin"];

const LEVEL_STYLES = {
  none:    { bg: "bg-slate-100",    fg: "text-slate-500",   label: "—" },
  view:    { bg: "bg-blue-50",      fg: "text-blue-700",    label: "View" },
  create:  { bg: "bg-violet-50",    fg: "text-violet-700",  label: "Create" },
  edit:    { bg: "bg-emerald-50",   fg: "text-emerald-700", label: "Edit" },
  approve: { bg: "bg-amber-50",     fg: "text-amber-700",   label: "Approve" },
  publish: { bg: "bg-rose-50",      fg: "text-rose-700",    label: "Publish" },
  admin:   { bg: "bg-[#0A192F]",    fg: "text-[#D4AF37]",   label: "Admin" },
};

export default function EventRoleSetupPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [rolesMaster, setRolesMaster] = useState([]);
  const [permsMaster, setPermsMaster] = useState({ permissions: [], by_category: {}, levels: LEVELS, sensitive_keys: [] });
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeRoleKey, setActiveRoleKey] = useState(null);
  const [search, setSearch] = useState("");

  // Initial load — fetch event + catalogues + templates + current config in parallel
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get(`/events/${eventId}`).catch(() => ({ data: null })),
      api.get("/rbac/roles-master"),
      api.get("/rbac/permissions-master"),
      api.get(`/events/${eventId}/roles/templates`),
      api.get(`/events/${eventId}/roles`),
    ])
      .then(([ev, rm, pm, tpl, cfg]) => {
        if (cancelled) return;
        setEvent(ev.data);
        setRolesMaster(rm.data.roles || []);
        setPermsMaster(pm.data);
        setTemplates(tpl.data.templates || []);
        setConfig(cfg.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // Lookups
  const rolesByKey = useMemo(() => Object.fromEntries(rolesMaster.map((r) => [r.key, r])), [rolesMaster]);
  const orderedRoles = useMemo(() => {
    if (!config) return [];
    return config.order.map((k) => ({
      key: k,
      master: rolesByKey[k],
      enabled: (config.enabled.find((e) => e.role_key === k) || {}).enabled !== false,
    }));
  }, [config, rolesByKey]);

  const filteredOrderedRoles = useMemo(() => {
    if (!search) return orderedRoles;
    const q = search.toLowerCase();
    return orderedRoles.filter((r) =>
      (r.master?.label || r.key).toLowerCase().includes(q)
    );
  }, [orderedRoles, search]);

  const permsByKey = useMemo(() => {
    const map = {};
    (config?.permissions || []).forEach((p) => {
      map[`${p.role_key}::${p.permission_key}`] = p.level;
    });
    return map;
  }, [config]);

  // --- Mutations ---
  const refreshConfig = async () => {
    const { data } = await api.get(`/events/${eventId}/roles`);
    setConfig(data);
  };

  const applyTemplate = async (template_key) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/events/${eventId}/roles/apply-template`, { template_key });
      setConfig(data);
      toast.success(`Template "${template_key}" applied`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = async (role_key, currentlyEnabled) => {
    setSaving(true);
    try {
      const endpoint = currentlyEnabled ? "disable" : "enable";
      await api.post(`/events/${eventId}/roles/${endpoint}`, { role_keys: [role_key] });
      await refreshConfig();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const moveRole = async (role_key, direction) => {
    if (!config) return;
    const order = [...config.order];
    const idx = order.indexOf(role_key);
    if (idx < 0) return;
    const j = direction === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= order.length) return;
    [order[idx], order[j]] = [order[j], order[idx]];
    setSaving(true);
    try {
      await api.patch(`/events/${eventId}/roles/order`, { ordered_roles: order });
      await refreshConfig();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setLevel = async (role_key, permission_key, level) => {
    setSaving(true);
    try {
      await api.patch(
        `/events/${eventId}/roles/${role_key}/permissions`,
        { permissions: { [permission_key]: level } }
      );
      await refreshConfig();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Find which template (if any) currently matches this event's config
  const activeTemplate = useMemo(() => {
    if (!config?.enabled?.length) return null;
    return config.enabled.find((e) => e.template_key)?.template_key || null;
  }, [config]);

  return (
    <DashboardLayout title="Event Role Setup">
      <div className="space-y-6" data-testid="event-role-setup-page">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/dashboard/events/${eventId}`}
            className="inline-flex items-center text-xs text-slate-500 hover:text-[#0A192F]"
          >
            <ArrowLeft className="h-3 w-3 mr-1" /> Back to event
          </Link>
          <span className="text-xs text-slate-400">/</span>
          <span className="text-xs font-semibold text-[#0A192F]">Role Setup</span>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-white p-6 border border-[#D4AF37]/30">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold flex items-center gap-1.5">
            <Layers className="h-3 w-3" /> Advanced Event RBAC · Phase A
          </p>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold">{event?.name || "Event"}</h1>
          <p className="text-sm text-slate-300 mt-1 max-w-2xl">
            Configure exactly which roles are active for this event and what
            each can do. Changes here apply only to this event — your other
            events stay untouched.
          </p>
          <div className="mt-4">
            <Link
              to={`/dashboard/events/${eventId}/rbac`}
              data-testid="rbac-go-phase-b"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md bg-[#D4AF37] text-[#0A192F] hover:bg-[#e7c455] transition-colors"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Phase B — Assignments, Audit, View-as &amp; Bulk Import
            </Link>
          </div>
        </div>

        {/* Templates row */}
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-sm font-bold text-[#0A192F] flex items-center gap-1.5">
                <BookOpen className="h-4 w-4 text-[#D4AF37]" /> Role Templates
              </p>
              <p className="text-[11px] text-slate-500">
                Pick a starting point — you can enable / disable / reorder afterwards.
              </p>
            </div>
            {activeTemplate && (
              <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded-full">
                Current: {activeTemplate}
              </span>
            )}
          </div>
          <div className="p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="role-template-grid">
            {templates.map((t) => {
              const isActive = activeTemplate === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => applyTemplate(t.key)}
                  disabled={saving}
                  data-testid={`template-${t.key}`}
                  className={`text-left rounded-lg border p-4 transition-all ${
                    isActive
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    {t.event_type}
                  </p>
                  <p className="mt-1 text-sm font-bold text-[#0A192F]">{t.label}</p>
                  <p className="mt-1 text-[11px] text-slate-500">{t.description}</p>
                  <p className="mt-2 text-[10px] text-slate-400">
                    {t.ordered_roles?.length || 0} roles
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Role hierarchy + matrix */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Hierarchy column */}
          <div className="lg:col-span-1 rounded-xl border border-slate-200 bg-white">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-[#0A192F]">Hierarchy</p>
                <p className="text-[11px] text-slate-500">Tap to open the permission matrix</p>
              </div>
              <button
                type="button"
                onClick={refreshConfig}
                className="text-[10px] inline-flex items-center gap-1 text-slate-500 hover:text-[#0A192F]"
                title="Refresh"
              >
                <RotateCcw className="h-3 w-3" />
              </button>
            </div>
            <div className="px-4 py-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search roles…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="role-search-input"
                  className="w-full pl-8 pr-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                />
              </div>
            </div>
            <ul className="divide-y divide-slate-100 max-h-[640px] overflow-auto" data-testid="role-hierarchy-list">
              {loading && <li className="px-5 py-8 text-xs text-slate-500 text-center">Loading…</li>}
              {!loading && filteredOrderedRoles.length === 0 && (
                <li className="px-5 py-8 text-xs text-slate-500 text-center">
                  No roles yet — apply a template above.
                </li>
              )}
              {filteredOrderedRoles.map((r, idx) => {
                const m = r.master;
                const isOpen = activeRoleKey === r.key;
                return (
                  <li
                    key={r.key}
                    data-testid={`role-row-${r.key}`}
                    className={`px-3 py-2 flex items-center gap-2 ${
                      isOpen ? "bg-[#D4AF37]/5 border-l-2 border-l-[#D4AF37]" : ""
                    } ${!r.enabled ? "opacity-50" : ""}`}
                  >
                    <span className="w-5 text-[10px] font-bold text-slate-400 text-right">{idx + 1}</span>
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => moveRole(r.key, "up")}
                        disabled={saving || idx === 0}
                        className="text-slate-400 hover:text-[#0A192F] disabled:opacity-30"
                        data-testid={`role-up-${r.key}`}
                      >
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveRole(r.key, "down")}
                        disabled={saving || idx === filteredOrderedRoles.length - 1}
                        className="text-slate-400 hover:text-[#0A192F] disabled:opacity-30"
                        data-testid={`role-down-${r.key}`}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveRoleKey(r.key)}
                      className="flex-1 text-left"
                    >
                      <p className="text-xs font-semibold text-[#0A192F]">
                        {m?.label || r.key}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        {m?.category} · scope: {m?.default_scope}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleRole(r.key, r.enabled)}
                      disabled={saving}
                      data-testid={`role-toggle-${r.key}`}
                      className={r.enabled ? "text-emerald-600" : "text-slate-400"}
                      title={r.enabled ? "Disable for this event" : "Enable for this event"}
                    >
                      {r.enabled ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Permission matrix */}
          <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white" data-testid="permission-matrix">
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="text-sm font-bold text-[#0A192F] flex items-center gap-1.5">
                <Settings2 className="h-4 w-4 text-[#D4AF37]" />
                Permission Matrix
                {activeRoleKey && (
                  <span className="text-xs font-normal text-slate-500">
                    · {rolesByKey[activeRoleKey]?.label || activeRoleKey}
                  </span>
                )}
              </p>
              <p className="text-[11px] text-slate-500">
                Level scale: <span className="font-mono">none → view → create → edit → approve → publish → admin</span>
              </p>
            </div>

            {!activeRoleKey && (
              <p className="px-5 py-12 text-center text-xs text-slate-500">
                Tap a role on the left to edit its permissions for this event.
              </p>
            )}

            {activeRoleKey && (
              <div className="max-h-[700px] overflow-auto">
                {Object.entries(permsMaster.by_category).map(([cat, perms]) => (
                  <div key={cat} className="border-b border-slate-100 last:border-0">
                    <div className="px-5 py-2 bg-slate-50 sticky top-0">
                      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{cat}</p>
                    </div>
                    <ul className="divide-y divide-slate-50">
                      {perms.map((p) => {
                        const sensitive = p.sensitive || permsMaster.sensitive_keys?.includes(p.key);
                        const currentLevel = permsByKey[`${activeRoleKey}::${p.key}`] || "none";
                        return (
                          <li
                            key={p.key}
                            data-testid={`perm-row-${p.key}`}
                            className="px-5 py-2.5 flex items-center justify-between gap-3"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold text-[#0A192F] flex items-center gap-1.5">
                                {p.label}
                                {sensitive && (
                                  <ShieldAlert
                                    className="h-3 w-3 text-amber-600"
                                    title="Sensitive — requires explicit organiser grant"
                                  />
                                )}
                              </p>
                              <p className="text-[10px] text-slate-400 font-mono">{p.key}</p>
                            </div>
                            <select
                              value={currentLevel}
                              onChange={(e) => setLevel(activeRoleKey, p.key, e.target.value)}
                              disabled={saving}
                              data-testid={`perm-select-${p.key}`}
                              className={`text-[11px] font-semibold rounded-md border px-2 py-1 ${LEVEL_STYLES[currentLevel]?.bg} ${LEVEL_STYLES[currentLevel]?.fg} border-slate-200`}
                            >
                              {LEVELS.map((lv) => (
                                <option key={lv} value={lv}>{LEVEL_STYLES[lv]?.label}</option>
                              ))}
                            </select>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Safety note */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 leading-relaxed">
          <p className="font-bold text-slate-800 flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5 text-amber-600" /> Safety guards in Phase A
          </p>
          <ul className="mt-1.5 space-y-1 list-disc pl-5">
            <li>Disabling a role here doesn't touch your other events.</li>
            <li>Sensitive permissions (Finance, Refund, Audit, Certificate Revoke, Final Result Lock) require explicit organiser grant.</li>
            <li>Every change is audit-logged with actor, timestamp and before/after.</li>
            <li>Phase B will add per-user assignments + role-preview "view as this role" + bulk import.</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
